import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Link as RouterLink } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import {
  LinkBreak,
  Play,
  ArrowSquareOut,
  Trash,
  PencilSimple,
  X,
  CheckCircle,
  FileArrowDown,
  Globe,
  TextT,
} from '@phosphor-icons/react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const SOURCE_LABELS = {
  description: { label: 'Beskrivelse', icon: TextT, color: 'text-blue-500' },
  short_description: { label: 'Kort beskrivelse', icon: TextT, color: 'text-blue-400' },
  download: { label: 'Nedlastbar fil', icon: FileArrowDown, color: 'text-purple-500' },
  external_url: { label: 'Ekstern URL', icon: ArrowSquareOut, color: 'text-orange-500' },
  permalink: { label: 'Produkt-URL', icon: Globe, color: 'text-red-500' },
};

const STATUS_TEXT = (s) => {
  if (!s) return '';
  if (s === 'timeout') return 'Tidsavbrudd';
  if (s === 'connection_error') return 'Kobling feilet';
  if (s === 'too_many_redirects') return 'For mange videresendinger';
  if (s.startsWith('error')) return 'Feil';
  if (/^\d+$/.test(s)) return `HTTP ${s}`;
  return s;
};

export default function LinksPage() {
  const [status, setStatus] = useState(null);
  const [summary, setSummary] = useState(null);
  const [broken, setBroken] = useState([]);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [sourceFilter, setSourceFilter] = useState('all');
  const [editing, setEditing] = useState(null); // { product_id, url, source }
  const [newUrl, setNewUrl] = useState('');

  const getHeaders = useCallback(() => ({ Authorization: `Bearer ${localStorage.getItem('token')}` }), []);

  const fetchStatus = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/api/links/status`, { headers: getHeaders() });
      setStatus(data);
    } catch {}
  }, [getHeaders]);

  const fetchSummary = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/api/links/summary`, { headers: getHeaders() });
      setSummary(data);
    } catch {}
  }, [getHeaders]);

  const fetchBroken = useCallback(async (p = 1) => {
    try {
      const { data } = await axios.get(`${API}/api/links/broken?page=${p}&limit=50`, { headers: getHeaders() });
      setBroken(data.items || []);
      setPages(data.pages || 1);
      setPage(data.page || 1);
    } catch {}
  }, [getHeaders]);

  useEffect(() => {
    const load = async () => {
      await Promise.all([fetchStatus(), fetchSummary(), fetchBroken(1)]);
      setLoading(false);
    };
    load();
    const interval = setInterval(() => {
      fetchStatus();
      fetchSummary();
    }, 5000);
    return () => clearInterval(interval);
  }, [fetchStatus, fetchSummary, fetchBroken]);

  const startScan = async () => {
    try {
      const { data } = await axios.post(`${API}/api/links/scan`, {}, { headers: getHeaders() });
      toast.success(data.message || 'Skanning startet');
      fetchStatus();
    } catch (err) {
      if (err.response?.status === 409) toast.error('Skanning kjører allerede');
      else toast.error('Kunne ikke starte skanning');
    }
  };

  const removeLink = async (item) => {
    if (item.source === 'permalink') {
      toast.error('Permalink kan ikke fjernes – håndter i WooCommerce');
      return;
    }
    if (!window.confirm(`Fjern lenken fra ${SOURCE_LABELS[item.source]?.label || 'produkt'}?`)) return;
    try {
      const { data } = await axios.post(`${API}/api/links/remove`, {
        product_id: item.product_id,
        url: item.url,
        original_url: item.original_url,
        source: item.source,
      }, { headers: getHeaders() });
      if (data.changed) {
        toast.success('Lenke fjernet (kun lokalt – godkjenn i Diff-visning)');
        await Promise.all([fetchBroken(page), fetchSummary()]);
      } else {
        toast.warning(data.message || 'Ingen endring');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Kunne ikke fjerne lenke');
    }
  };

  const startReplace = (item) => {
    if (item.source === 'permalink') {
      toast.error('Permalink kan ikke erstattes her – håndter i WooCommerce');
      return;
    }
    setEditing(item);
    setNewUrl('');
  };

  const submitReplace = async () => {
    if (!newUrl || !newUrl.startsWith('http')) {
      toast.error('Skriv inn en gyldig URL (http:// eller https://)');
      return;
    }
    try {
      const { data } = await axios.post(`${API}/api/links/replace`, {
        product_id: editing.product_id,
        url: editing.url,
        original_url: editing.original_url,
        new_url: newUrl,
        source: editing.source,
      }, { headers: getHeaders() });
      if (data.changed) {
        toast.success('Lenke erstattet (kun lokalt)');
        setEditing(null);
        setNewUrl('');
        await Promise.all([fetchBroken(page), fetchSummary()]);
      } else {
        toast.warning(data.message || 'Ingen endring');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Kunne ikke erstatte');
    }
  };

  const clearAll = async () => {
    if (!window.confirm('Tøm listen over ødelagte lenker? Produkter endres ikke.')) return;
    try {
      await axios.delete(`${API}/api/links/broken`, { headers: getHeaders() });
      toast.success('Listen tømt');
      await Promise.all([fetchBroken(1), fetchSummary()]);
    } catch {
      toast.error('Feilet');
    }
  };

  if (loading) {
    return (
      <div className="space-y-4" data-testid="links-loading">
        <div className="h-8 w-48 bg-muted animate-pulse rounded" />
        <div className="h-64 bg-muted animate-pulse rounded-md" />
      </div>
    );
  }

  const progress = status?.total > 0 ? Math.round((status.scanned / status.total) * 100) : 0;
  const filtered = sourceFilter === 'all' ? broken : broken.filter((b) => (b.source || 'description') === sourceFilter);

  return (
    <div className="space-y-6" data-testid="links-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="font-heading text-3xl sm:text-4xl tracking-tight font-extrabold flex items-center gap-2">
            <LinkBreak size={28} weight="bold" className="text-primary sm:hidden" />
            <LinkBreak size={36} weight="bold" className="text-primary hidden sm:inline" /> Ødelagte lenker
          </h1>
          <p className="text-xs sm:text-sm text-muted-foreground mt-1">
            Skanner alle produkter for lenker som gir 404 / timeout – i beskrivelse, nedlastinger, ekstern URL og permalink.
          </p>
        </div>
        <div className="flex gap-2 flex-wrap">
          {broken.length > 0 && (
            <Button variant="outline" onClick={clearAll} data-testid="clear-broken-btn" className="gap-2">
              <Trash size={14} /> Tøm liste
            </Button>
          )}
          <Button
            onClick={startScan}
            disabled={status?.running}
            data-testid="start-link-scan-btn"
            className="gap-2"
          >
            <Play size={14} weight="bold" /> {status?.running ? 'Skanner...' : 'Start skanning'}
          </Button>
        </div>
      </div>

      {/* Status Card */}
      <Card className="border border-border" data-testid="scan-status-card">
        <CardContent className="p-6">
          <div className="flex items-center gap-4 mb-4">
            <div className={`w-3 h-3 rounded-full ${status?.running ? 'bg-success animate-pulse' : 'bg-muted-foreground'}`} />
            <span className="text-sm font-medium">
              {status?.running ? 'Kjører' : status?.completed_at ? 'Fullført' : 'Ikke startet'}
            </span>
            {status?.running && <span className="text-xs text-muted-foreground">Timeout 10s pr URL</span>}
          </div>

          {(status?.running || status?.total > 0) && (
            <div className="space-y-2">
              <div className="flex justify-between text-sm">
                <span>{status?.scanned || 0} av {status?.total || 0} produkter skannet</span>
                <span className="font-mono">{progress}%</span>
              </div>
              <Progress value={progress} className="h-2" />
            </div>
          )}

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-4">
            <div className="text-center p-3 bg-muted/50 rounded-md">
              <p className="font-heading text-xl font-bold">{status?.scanned || 0}</p>
              <p className="text-xs text-muted-foreground">Produkter skannet</p>
            </div>
            <div className="text-center p-3 bg-muted/50 rounded-md">
              <p className="font-heading text-xl font-bold">{status?.urls_checked || 0}</p>
              <p className="text-xs text-muted-foreground">Unike URL-er sjekket</p>
            </div>
            <div className="text-center p-3 bg-muted/50 rounded-md">
              <p className="font-heading text-xl font-bold text-destructive">{summary?.total_broken || 0}</p>
              <p className="text-xs text-muted-foreground">Ødelagte lenker</p>
            </div>
            <div className="text-center p-3 bg-muted/50 rounded-md">
              <p className="font-heading text-xl font-bold text-warning">{summary?.affected_products || 0}</p>
              <p className="text-xs text-muted-foreground">Berørte produkter</p>
            </div>
          </div>

          {/* Breakdown by source */}
          {summary?.by_source?.length > 0 && (
            <div className="flex flex-wrap gap-2 mt-4 pt-4 border-t border-border">
              {summary.by_source.map((s) => {
                const meta = SOURCE_LABELS[s.source] || { label: s.source };
                const Icon = meta.icon;
                return (
                  <Badge key={s.source} variant="outline" className="gap-1" data-testid={`source-badge-${s.source}`}>
                    {Icon && <Icon size={12} className={meta.color} />}
                    {meta.label}: <strong>{s.count}</strong>
                  </Badge>
                );
              })}
              {summary.by_status?.slice(0, 4).map((s) => (
                <Badge key={s.status} variant="secondary" className="text-xs">
                  {STATUS_TEXT(s.status)}: {s.count}
                </Badge>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Filter */}
      {broken.length > 0 && (
        <div className="flex items-center gap-3" data-testid="links-filter">
          <span className="text-sm text-muted-foreground">Filtrer:</span>
          <Select value={sourceFilter} onValueChange={setSourceFilter}>
            <SelectTrigger className="w-full sm:w-[220px]" data-testid="source-filter">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Alle kilder</SelectItem>
              <SelectItem value="description">Beskrivelse</SelectItem>
              <SelectItem value="short_description">Kort beskrivelse</SelectItem>
              <SelectItem value="download">Nedlastbare filer</SelectItem>
              <SelectItem value="external_url">Ekstern URL</SelectItem>
              <SelectItem value="permalink">Produkt-URL</SelectItem>
            </SelectContent>
          </Select>
          <span className="text-xs text-muted-foreground">{filtered.length} treff</span>
        </div>
      )}

      {/* Broken links list */}
      {filtered.length === 0 ? (
        <Card className="border border-border">
          <CardContent className="p-8 text-center text-muted-foreground">
            <CheckCircle size={32} className="mx-auto mb-2 text-success" />
            <p className="text-sm">
              {broken.length === 0 && !status?.completed_at
                ? 'Ingen skanning kjørt ennå. Start en skanning for å finne ødelagte lenker.'
                : 'Ingen ødelagte lenker funnet – flott!'}
            </p>
          </CardContent>
        </Card>
      ) : (
        <Card className="border border-border">
          <CardHeader>
            <CardTitle className="font-heading text-lg">Funn ({filtered.length})</CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <div className="divide-y divide-border">
              {filtered.map((item, idx) => {
                const meta = SOURCE_LABELS[item.source || 'description'] || { label: item.source, icon: TextT };
                const Icon = meta.icon;
                const isEditing = editing && editing.product_id === item.product_id && editing.url === item.url && editing.source === item.source;
                return (
                  <div
                    key={`${item.product_id}-${item.url}-${item.source}-${idx}`}
                    className="p-4 hover:bg-muted/30 transition-colors"
                    data-testid={`broken-link-row-${item.product_id}`}
                  >
                    <div className="flex items-start justify-between gap-4">
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <RouterLink
                            to={`/products/${item.product_id}`}
                            className="font-medium text-sm hover:underline truncate"
                            data-testid={`product-link-${item.product_id}`}
                          >
                            {item.product_name || `Produkt #${item.product_id}`}
                          </RouterLink>
                          {Icon && (
                            <Badge variant="outline" className="gap-1 text-xs">
                              <Icon size={11} className={meta.color} /> {meta.label}
                            </Badge>
                          )}
                          {item.is_internal && <Badge variant="secondary" className="text-xs">Intern</Badge>}
                          <Badge variant="destructive" className="text-xs font-mono">
                            {STATUS_TEXT(item.status)}
                          </Badge>
                        </div>
                        <a
                          href={item.url}
                          target="_blank"
                          rel="noreferrer"
                          className="text-xs text-muted-foreground font-mono break-all hover:text-primary"
                          data-testid={`broken-url-${item.product_id}`}
                        >
                          {item.url}
                        </a>
                        {item.download_name && (
                          <p className="text-xs text-muted-foreground mt-1">
                            Filnavn: <span className="font-medium">{item.download_name}</span>
                          </p>
                        )}
                      </div>

                      {!isEditing && (
                        <div className="flex gap-2 shrink-0">
                          {item.source !== 'permalink' ? (
                            <>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => startReplace(item)}
                                className="gap-1"
                                data-testid={`replace-btn-${item.product_id}`}
                              >
                                <PencilSimple size={12} /> Erstatt
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => removeLink(item)}
                                className="gap-1 text-destructive"
                                data-testid={`remove-btn-${item.product_id}`}
                              >
                                <Trash size={12} /> Fjern
                              </Button>
                            </>
                          ) : (
                            <span className="text-xs text-muted-foreground italic whitespace-nowrap" data-testid={`permalink-note-${item.product_id}`}>
                              Må håndteres i WooCommerce
                            </span>
                          )}
                        </div>
                      )}
                    </div>

                    {isEditing && (
                      <div className="mt-3 flex gap-2 items-center" data-testid={`replace-form-${item.product_id}`}>
                        <Input
                          placeholder="Ny URL (https://...)"
                          value={newUrl}
                          onChange={(e) => setNewUrl(e.target.value)}
                          className="font-mono text-xs"
                          data-testid="new-url-input"
                          autoFocus
                        />
                        <Button size="sm" onClick={submitReplace} data-testid="submit-replace-btn">
                          <CheckCircle size={14} />
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => { setEditing(null); setNewUrl(''); }} data-testid="cancel-replace-btn">
                          <X size={14} />
                        </Button>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Pagination */}
      {pages > 1 && (
        <div className="flex items-center justify-center gap-2" data-testid="pagination">
          <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => fetchBroken(page - 1)}>
            Forrige
          </Button>
          <span className="text-sm text-muted-foreground">Side {page} av {pages}</span>
          <Button variant="outline" size="sm" disabled={page >= pages} onClick={() => fetchBroken(page + 1)}>
            Neste
          </Button>
        </div>
      )}
    </div>
  );
}
